import { useEffect, useState, useRef, useMemo } from "react";
import { Box, IconButton, Typography, LinearProgress, Checkbox, Table, TableBody, TableRow, TableCell, TableContainer, Paper, TextField} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { DataGrid } from '@mui/x-data-grid';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import { apiClient } from "../helper/api";
import useAuth, { authData } from "../context/AuthProvider";
import formatPrice from "../helper/currencyFormatter";
import Swal from "sweetalert2";
import { safeMoment, writeXlsxFile } from "../helper/utils";
import { StateReportModel } from "../models/StateReportModel";
import moment from "moment";
import { StateReportPreviewDialog } from "../components/Elements/StateReportPreviewDialog";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
 
interface Partner {
  partnersId: number;
  customerName: string;
}

export const ClientStateReports: React.FC<{ currentUser: authData | undefined }> = ({ currentUser }) => {
  const [loading, setloading] = useState(false);
  const { user } = useAuth();
  const [itemList, setItemList] = useState<StateReportModel[]>([]);
  const [pageSize, setPageSize] = useState(50);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortModel, setSortModel] = useState([{ field: "", sort: "" }]);
  const [filterModel, setFilterModel] = useState<{ items: { columnField: string, value: string, operatorValue: string }[] }>({ items: [] });
  const [selectedHomeState, setSelectedHomeState] = useState('');
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [selectedPartner, setSelectedPartner] = useState('');
  const [partners, setPartners] = useState<Partner[]>([]);
  const [selectedTab, setSelectedTab] = useState<number>(1);
  const selectedPartnerRef = useRef<string>('0');
  const selectedStateRef = useRef<number>(0);
  const startDateRef = useRef<string>('');
  const endDateRef = useRef<string>('');
  const token = user?.token;
  const initialStatedata = { stateId: 0, stateName: "" };
  const [States, setStates] = useState([initialStatedata]);
  const [summaryData, setSummaryData] = useState<{DueInNext5Days: number, DueToday: number, PastDue: number}>({DueInNext5Days: 0, DueToday: 0, PastDue: 0});
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedStateReport, setSelectedStateReport] = useState<StateReportModel | null>(null);

  const formatLabel = (str: string) => {
    return str
      .replace(/([A-Z0-9])/g, ' $1')   // space before capital letters & numbers
      .replace(/[_\-]+/g, ' ')         // replace _ and - with space
      .replace(/\s+/g, ' ')            // collapse multiple spaces
      .trim()                          // remove leading/trailing spaces
      .replace(/^./, s => s.toUpperCase()); // capitalize first letter
  };

  const fetchStateData = () => {
    apiClient("/States/getStates", {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
    })
      .then((responseJson) => {
        if (!responseJson) return;
        setStates(responseJson.data);
      });
  };

  const getPartnersList = () => {
    apiClient("/customer/getPartnersList", {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
    })
      .then((responseJson) => {
        if (!responseJson) return;
        if (responseJson.data && responseJson.data.length > 0) {
          setPartners(responseJson.data);
        } else {
          setPartners([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching partners:", error);
        setPartners([]);
      });
  };

  const getSummaryData = () => {
    apiClient("/StateReport/StateReportSummary", {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
    })
    .then((responseJson) => {
      if (!responseJson) return;
      if (responseJson.status && responseJson.data) {
        setSummaryData(responseJson.data);
      } else {
        setSummaryData({DueInNext5Days: 0, DueToday: 0, PastDue: 0});
      }
    })
    .catch((error) => {
      console.error("Error fetching summary data:", error);
      setSummaryData({DueInNext5Days: 0, DueToday: 0, PastDue: 0});
    });
  };

  useEffect(() => {
    getSummaryData();
    fetchStateData();
    getPartnersList();
  }, []);

  useEffect(() => {
    fetchData();
  }, [pageNumber, pageSize, filterModel, sortModel, selectedTab]);

  const fetchData = async () => {
    setloading(true);
    const res = await fetchDataMain(pageSize, pageNumber);
    if (res) {
      setItemList(res.data ?? []);
      setTotalCount(res.totalCount ?? 0);
    }
    setloading(false);
  };

  const fetchDataMain = async (_pageSize: number, _pageNumber: number) => {
    try {
      const requestBody = {
        pageSize: _pageSize || 50,
        currentPage: _pageNumber || 1,
        partnerId: selectedPartnerRef.current || '',
        stateId: selectedStateRef.current || '',
        startDate: startDateRef.current || '',
        endDate: endDateRef.current || '',
        field: sortModel?.[0]?.['field'] || '',
        sort: sortModel?.[0]?.['sort'] || '',
        fieldName: filterModel?.items?.[0]?.['columnField'] || '',
        value: filterModel?.items?.[0]?.['value'] || '',
        operator: filterModel?.items?.[0]?.['operatorValue'] || '',
        selectedTab: selectedTab || 0
      };

      const res = await apiClient(`/StateReport/ClientStateReports`, {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        }),
        body: JSON.stringify(requestBody),
      });
      if (!res || !res.status) return null;
      return res;
    } catch (error) {
      console.error("Error fetching data:", error);
      return null;
    }
  };

  const RunSearch = async () => {
    setloading(true);
    startDateRef.current = startDate ? startDate.format('MM/DD/YYYY') : '';
    endDateRef.current = endDate ? endDate.format('MM/DD/YYYY') : '';
    selectedPartnerRef.current = selectedPartner;
    selectedStateRef.current = Number(selectedHomeState);
    fetchData();
  };

  const ClearFilter = () => {
    setloading(true);
    setSelectedHomeState('');
    setStartDate(null);
    setEndDate(null);
    setSelectedPartner('');
    startDateRef.current = '';
    endDateRef.current = '';
    selectedPartnerRef.current = '0';
    selectedStateRef.current = 0;
    fetchData();
  };

  const handleExport = async () => {
    try {
      setloading(true);

      // Use current table data instead of fetching all data
      const exportData = itemList;

      if (!exportData || exportData.length === 0) {
        Swal.fire({
          icon: 'error',
          title: 'Export Failed',
          text: 'No data available to export',
          confirmButtonText: 'OK'
        });
        setloading(false);
        return;
      }

      // Filter columns to exclude "Done", "View", and hidden columns
      // Only include "Client" column if there are multiple partners
      const exportableColumns = columns.filter((column) => {
        // Exclude hidden columns
        if (column.hide) return false;
        // Exclude "Done" and "View" columns
        if (column.field === 'isDoneByClient' || column.field === 'view') return false;
        // Only include "Client" if there are multiple partners
        if (column.field === 'client' && partners.length <= 1) return false;
        return true;
      });

      // Get headers from filtered columns
      const headers = exportableColumns.map((column) => column.headerName || '');

      // Prepare data for export - match UI display logic
      const exportRows = exportData.map((item: StateReportModel) => {
        const row: any[] = [];
        
        exportableColumns.forEach((column) => {
          const field = column.field;
          let value: any = '';
          
          if (field === 'dueDate') {
            value = item.dueDate ? safeMoment(item.dueDate).format("MM/DD/YYYY") : '';
          } else if (field === 'stateName') {
            value = item.stateName || '';
          } else if (field === 'licensee') {
            value = item.licensee || '';
          } else if (field === 'client') {
            value = item.client || '';
          } else if (field === 'name') {
            value = item.name || '';
          } else if (field === 'period') {
            value = item.period || '';
          } else if (field === 'feeDue') {
            const feeDue = item.feeDue || '';
            const numericValue = typeof feeDue === 'string' 
              ? parseFloat(feeDue.replace(/[,\$]/g, "").trim()) 
              : parseFloat(String(feeDue));
            value = isNaN(numericValue) ? feeDue : formatPrice(numericValue.toString());
          } else {
            // Fallback to direct property access
            value = (item as any)[field] || '';
          }
          
          row.push(value);
        });
        
        return row;
      });

      // Combine headers and data
      const exportArray = [headers, ...exportRows];

      // Generate filename with timestamp
      const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss');
      const filename = `StateReports_${exportData.length}_${timestamp}.xlsx`;

      // Export to Excel
      await writeXlsxFile(exportArray, filename);

      // Show success popup
      Swal.fire({
        icon: 'success',
        title: 'Export Successful',
        text: `Successfully Exported.`,
        confirmButtonText: 'OK'
      });
    } catch (error) {
      console.error('Export error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Export Failed',
        text: 'An error occurred while exporting the data',
        confirmButtonText: 'OK'
      });
    } finally {
      setloading(false);
    }
  };

  const handleDoneChange = (stateReportId: number, status: boolean) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You want to mark this report as ${status ? 'Completed' : 'Revised'}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
    }).then((result) => {
      if (result.isConfirmed) {
        setloading(true);
        apiClient(`/StateReport/UpdateClientStateReportStatus`, {
          method: "POST",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          }),
          body: JSON.stringify({
            stateReportId: stateReportId,
            status: status ? 'Completed' : 'Revised',
          }),
        }).then((res) => {
          if (!res) return;
          if (res.status) {
            Swal.fire({
              title: 'Success!',
              text: `The report has been marked as ${status ? 'Completed' : 'Revised'}.`,
              icon: 'success',
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: res.message,
              icon: 'error',
            });
          }
        }).catch((error) => {
          console.error(`Error marking report as ${status ? 'Completed' : 'Revised'}:`, error);
          Swal.fire({
            title: 'Error!',
            text: `Failed to mark report as ${status ? 'Completed' : 'Revised'}.`,
            icon: 'error',
          });
        }).finally(() => {
          fetchData();
          getSummaryData();
          setloading(false);
        });
      }
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'customerPolicyId',
      hide: true,
    },
    {
      field: 'isDoneByClient',
      headerName: 'Done',
      width: 100,
      minWidth: 100,
      flex: 0.5,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Checkbox
          checked={params.row.isDoneByClient == 'true' || false}
          onChange={(e) => handleDoneChange(params.row.stateReportId, e.target.checked)}
          sx={{ padding: 0 }}
        />
      ),
    },
    {
      field: 'dueDate',
      hideSortIcons: true,
      type: 'string',
      headerName: 'Due Date',
      flex: 0.5,
      align: 'center',
      headerAlign: 'center',
      minWidth: 180,
      width: 180,
      renderCell: (params) => {
        if (!params.value) {
          return <span style={{ color: 'inherit' }}>{params.value}</span>;
        }

        let dueDate;
        try {
          dueDate = new Date(params.value);
          if (isNaN(dueDate.getTime())) {
            throw new Error('Invalid date');
          }
        } catch (error) {
          console.error('Invalid date value:', params.value, error);
          return <span style={{ color: 'inherit' }}>{params.value}</span>;
        }

        const currentDate = new Date();
        const isLate = dueDate < currentDate;

        return (
          <span style={{ color: isLate ? 'red' : 'inherit' }}>
            {safeMoment(dueDate).format("MM/DD/YYYY")}
          </span>
        );
      }
    },
    {
      field: 'stateName',
      headerName: 'State',
      flex: 1,
      minWidth: 100,
      width: 100,
    },
    {
      field: 'licensee',
      headerName: 'Licensee',
      flex: 1,
      minWidth: 200,
      width: 200,
    },
    ...(partners.length > 1 ? [{
      field: 'client',
      headerName: 'Client',
      flex: 1,
      minWidth: 400,
      width: 400,
    } as GridColDef] : []),
    {
      field: 'name',
      headerName: 'Report Name',
      flex: 1,
      minWidth: 380,
      width: 380,
    },
    {
      field: 'period',
      headerName: 'Period',
      flex: 1,
      minWidth: 100,
      width: 100,
    },
    {
      field: 'feeDue',
      headerName: 'Amount Due',
      flex: 1,
      minWidth: 130,
      width: 130,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        let feeDue = params.value || '';
        const numericValue = parseFloat(feeDue);
        return isNaN(numericValue) ? feeDue : `${formatPrice(numericValue.toString())}`;
      }
    },
    {
      field: 'view',
      headerName: 'View',
      flex: 0.5,
      minWidth: 80,
      width: 80,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          onClick={() => {
            setSelectedStateReport(params.row);
            setPreviewDialogOpen(true);
          }}
        >
          <RemoveRedEyeOutlinedIcon />
        </IconButton>
      ),
    },
  ];

  // Summary table data
  return (
    <Box sx={{ width: '100%' }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={{ position: "fixed", top: "0", left: "0", zIndex: "9990", width: "100%" }}>
        {loading && <LinearProgress color="info" sx={{ backgroundColor: "#132D3D" }} />}
      </div>
      <Box sx={{ width: '100%', paddingBottom: '10px' }} className="customer-header">
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          marginBottom: '20px'
        }}>
          {/* Left side with Title and Welcome message */}
          <Box>
            <Typography 
              variant="h1" 
              component="h1" 
              sx={{ 
                fontSize: '40px',
                fontWeight: 'normal',
                marginBottom: '8px',
                fontFamily: 'serif'
              }}
            >
              State Reports
            </Typography>
              
            <Typography 
              variant="body1" 
              sx={{ 
                fontSize: '16px',
                color: '#000',
                fontFamily: 'serif',
                textAlign: 'left'
              }}
            >
              <b>Welcome, {currentUser?.userFirstName} {currentUser?.userlastName}!</b>
            </Typography>
          </Box>

          {/* Right side with Date and Summary */}
          <Box sx={{ textAlign: 'right' }}>
            <Typography
              variant="body1"
              sx={{
                fontSize: '16px',
                color: '#000',
                marginBottom: '10px',
                fontFamily: 'serif',
                textAlign: 'right',
              }}
            >
              <strong>Today's Date Is: </strong>
              <strong>{moment().format('dddd, MMMM D, YYYY')}</strong>
            </Typography>

            {/* Summary Box */}
            <TableContainer
              component={Paper}
              sx={{
                maxWidth: 250,
                display: 'inline-block',
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              <Table
                size="small"
                sx={{
                  borderCollapse: 'collapse',
                  '& td': {
                    border: '4px solid white !important', // ✅ Thin white cell borders
                    padding: '4px 10px',
                    textAlignLast: 'start'
                  },
                }}
              >
                {selectedTab === 1 ? (
                  <TableBody>
                  {Object.entries(summaryData).map(([key, value]) => (
                    <TableRow key={key}>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          backgroundColor: '#b3d1ff', // ✅ Left cell = darker blue
                          color: '#000',
                          width: '70%',
                        }}
                      >
                        {formatLabel(key)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 600,
                          backgroundColor: '#e6f0ff', // ✅ Right cell = lighter blue
                          color: key == 'pastDue' ? '#d32f2f' : '#000',
                        }}
                      >
                        {value ? value.toString() : '0'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                  ) : (
                  null
                )}
              </Table>
            </TableContainer>
          </Box>
        </Box>

        {/* Instructional Text */}
        <Box sx={{ textAlign: 'left', marginBottom: '20px' }}>
          <Typography 
            variant="body1" 
            sx={{ 
              fontSize: '16px',
              color: '#000',
              fontFamily: 'serif',
              lineHeight: 1.6,
              textAlign: 'left'
            }}
          >
            {selectedTab === 1 ? (
              <>
                Below, please find a list of State Reports that our team has processed on your behalf which now requires action from your team. You may review the details and instructions for each report by selecting the <strong>View</strong> button to the right. Check <strong>Done</strong> to mark an item as Completed and move it to your Completed Queue.
              </>
            ) : (
              <>
                Below, please find a historical list of your Completed State Reports. You may review the details and attached documents on any report by selecting the <strong>View</strong> button to the right.
              </>
            )}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 2,
          ml: '186px',
        }}
      >
        <div className="col-6 text-center">
          <button
            type="button"
            className={`btn-tab btn ${selectedTab === 1 ? "btn-Selected" : "btn-primary1"}`}
            onClick={() => setSelectedTab(1)}
            style={{
              padding: '10px 40px',
            }}
          >
            Outstanding
          </button>
        </div>
        <div className="col-6 text-center">
          <button
            type="button"
            className={`btn-tab btn ${selectedTab === 2 ? "btn-Selected" : "btn-primary"}`}
            onClick={() => setSelectedTab(2)}
            style={{
              padding: '10px 40px',
            }}
          >
            Completed
          </button>
        </div>
      </Box>

      {/* Filter Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 2,
          ml: '186px',
        }}
      >
        {/* Home State Dropdown */}
        <select
          style={{
            width: '300px',
            height: '40px',
            padding: '8px 12px',
            fontSize: '14px',
            border: '1px solid #ccc',
            borderRadius: '20px',
          }}
          value={selectedHomeState}
          onChange={(e) => setSelectedHomeState(e.target.value)}
        >
          <option value="">Home State</option>
          {States.map((state) => (
            <option key={state.stateId} value={state.stateId}>
              {state.stateName}
            </option>
          ))}
        </select>

        {/* Start Date and End Date Inputs */}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={(newValue) => setStartDate(newValue)}
            inputFormat="MM/DD/YYYY"
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Start Date"
                sx={{
                  width: '300px',
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    borderRadius: '20px',
                    fontSize: '14px',
                  },
                }}
              />
            )}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={(newValue) => setEndDate(newValue)}
            inputFormat="MM/DD/YYYY"
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="End Date"
                sx={{
                  width: '300px',
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    borderRadius: '20px',
                    fontSize: '14px',
                  },
                }}
              />
            )}
          />
        </LocalizationProvider>

        {/* Client Dropdown */}
        {partners.length > 1 && (
          <select
            style={{
              width: '300px',
              height: '40px',
              padding: '8px 12px',
              fontSize: '14px',
              border: '1px solid #ccc',
              borderRadius: '20px',
            }}
            value={selectedPartner}
            onChange={(e) => setSelectedPartner(e.target.value)}
          >
            <option value="">Select Client</option>
            {partners.map((partner) => (
              <option key={partner.partnersId} value={partner.partnersId}>
                {partner.customerName}
              </option>
            ))}
          </select>
        )}

        {/* Search Button */}
        <div>
          <button
            type="button"
            style={{
              width: '100px',
              height: '40px',
              backgroundColor: '#2099d2',
              color: '#fff',
              border: 'none',
              borderRadius: '20px',
              cursor: 'pointer',
            }}
            onClick={RunSearch}
            className="active-btn1-blue-tabs"
          >
            Search
          </button>
        </div>

        {/* Clear Button */}
        <div>
          <button
            type="button"
            style={{
              width: '100px',
              height: '40px',
              backgroundColor: '#f5f5f5',
              color: '#333',
              border: '1px solid #ccc',
              borderRadius: '20px',
              cursor: 'pointer',
            }}
            onClick={ClearFilter}
            className="btn-clear-refresh btn"
          >
            Clear
          </button>
        </div>

        {/* Refresh Button */}
        <div>
          <button
            type="button"
            style={{
              width: '100px',
              height: '40px',
              backgroundColor: '#f5f5f5',
              color: '#333',
              border: '1px solid #ccc',
              borderRadius: '20px',
              cursor: 'pointer',
            }}
            onClick={() => window.location.reload()}
            className="btn-clear-refresh btn"
          >
            Refresh
          </button>
        </div>

        {/* Export Button */}
        <div>
          <button
            type="button"
            style={{
              width: '100px',
              height: '40px',
              backgroundColor: '#2099d2',
              color: '#fff',
              border: 'none',
              borderRadius: '20px',
              cursor: 'pointer',
            }}
            onClick={handleExport}
            className="active-btn1-blue-tabs"
          >
            Export
          </button>
        </div>
      </Box>
      <Box className="shadow rounded" sx={{ height: '500px', width: '100%', backgroundColor: '#FFF', padding: '0 !important', overflow: 'hidden', border: '1px solid #8cc5e6' }}>
        <DataGrid
          getRowId={(r) => r.rowNum}
          classes={{
            columnHeaders: "MuiDataGridPro-columnHeaders custom-header",
            virtualScroller: "MuiDataGridPro-virtualScroller",
          }}
          rows={itemList}
          columns={columns}
          rowCount={totalCount}
          pageSize={pageSize}
          pagination
          paginationMode="server"
          onPageChange={(newPage: number) => setPageNumber(newPage + 1)}
          rowsPerPageOptions={[10, 25, 50]}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          filterMode="server"
          onFilterModelChange={(newFilterModel: any) => setFilterModel(newFilterModel)}
          sortingMode="server"
          sortingOrder={['asc', 'desc']}
          onSortModelChange={(newSortModel: any) => setSortModel(newSortModel)}
          hideFooterSelectedRowCount
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              whiteSpace: "normal",
              lineHeight: "normal",
            },
            fontFamily: "'Proxima Nova', sans-serif",
            border: "0px",
            fontSize: "16px",
          }}
        />
      </Box>

      {/* State Report Preview Dialog */}
      <StateReportPreviewDialog
        open={previewDialogOpen}
        onClose={() => {
          setPreviewDialogOpen(false);
          setSelectedStateReport(null);
        }}
        stateReport={selectedStateReport}
        selectedTab={selectedTab}
        states={States}
        onMarkAsDone={() => {
          fetchData();
          getSummaryData();
        }}
      />
    </Box>
  );
};
